import styled from 'styled-components';
import { vars, Button, gtagEvent } from '@agendor/ui';
import { Booking, ClientSubscriptionInfo } from '../booking-service';
import { ButtonGroup } from '../shared-styles';
import { formatPrice, formatTime, formatBookingDate } from '../formatting';
import * as AppRouting from '../app-routing';

const SubscriptionCard = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 16px;
    margin-bottom: 16px;
    background-color: #fff;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    font-size: 0.9rem;
    color: ${vars.foregroundColor};
`;

const SubscriptionCardRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const SubscriptionCardDetail = styled.span`
    font-size: 0.85rem;
    color: #666;
`;

const BookingCard = styled.div`
    padding: 16px;
    border: 1px solid ${vars.dividerColor};
    border-radius: 8px;
    margin-bottom: 12px;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;

    @media (max-width: 480px) {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const BookingInfo = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const BookingServiceName = styled.div`
    font-weight: 600;
    font-size: 1rem;
    color: ${vars.foregroundColor};
`;

const BookingNotes = styled.div`
    font-size: 0.85rem;
    color: #666;
    font-style: italic;
    margin-top: 2px;
`;

const BookingMeta = styled.div`
    font-size: 0.9rem;
    color: #888;
`;

const BookingActions = styled.div`
    flex-shrink: 0;
`;

type Props = {
    bookings: Booking[];
    bookingsError: string | null;
    isLoadingBookings: boolean;
    clientSubscription: ClientSubscriptionInfo | null;
    onNewBooking: () => void;
    onCancelBooking: (booking: Booking) => void;
    onCancelSubscription: () => void;
    onRetryLoadBookings: () => void;
};

export default function BookingsSection({
    bookings,
    bookingsError,
    isLoadingBookings,
    clientSubscription,
    onNewBooking,
    onCancelBooking,
    onCancelSubscription,
    onRetryLoadBookings,
}: Props) {
    return (
        <>
            {clientSubscription && (
                <SubscriptionCard data-testid="subscription-card">
                    <SubscriptionCardRow>
                        <strong>
                            {clientSubscription.status === 'overdue' ? 'Assinatura inadimplente' : 'Assinatura ativa'} —{' '}
                            {formatPrice(clientSubscription.priceInCents)}/mês
                        </strong>
                    </SubscriptionCardRow>
                    {clientSubscription.creditCard && (
                        <SubscriptionCardDetail>
                            Cartão: {clientSubscription.creditCard.brand} final {clientSubscription.creditCard.last4}
                        </SubscriptionCardDetail>
                    )}
                    {clientSubscription.nextBillingDate && (
                        <SubscriptionCardDetail>
                            Próxima cobrança:{' '}
                            {new Date(clientSubscription.nextBillingDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                        </SubscriptionCardDetail>
                    )}
                    <Button data-testid="cancel-subscription-btn" danger onClick={onCancelSubscription}>
                        Cancelar
                    </Button>
                </SubscriptionCard>
            )}
            <h2 data-testid="screen-bookings">Meus Agendamentos</h2>
            <div style={{ marginTop: 16 }}>
                {bookingsError ? (
                    <div
                        style={{
                            textAlign: 'center',
                            padding: '40px 0',
                            color: '#888',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 12,
                            alignItems: 'center',
                        }}
                    >
                        <div>{bookingsError}</div>
                        <Button onClick={onRetryLoadBookings} disabled={isLoadingBookings}>
                            {isLoadingBookings ? 'Carregando...' : 'Tentar novamente'}
                        </Button>
                    </div>
                ) : isLoadingBookings ? (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>Carregando...</div>
                ) : bookings.length > 0 ? (
                    bookings.map((booking) => (
                        <BookingCard
                            key={booking.id}
                            style={
                                booking.payment && !booking.payment.paid
                                    ? { backgroundColor: vars.warningTintSoft }
                                    : undefined
                            }
                        >
                            <BookingInfo>
                                <BookingServiceName
                                    style={
                                        booking.isRecurring
                                            ? { display: 'flex', alignItems: 'center', gap: 6 }
                                            : undefined
                                    }
                                >
                                    <span
                                        style={
                                            booking.isRecurring
                                                ? {
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      whiteSpace: 'nowrap',
                                                      minWidth: 0,
                                                  }
                                                : undefined
                                        }
                                    >
                                        {booking.services.map((s) => s.name).join(', ')}
                                    </span>
                                    {booking.isRecurring && (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={2}
                                            style={{ width: 14, height: 14, flexShrink: 0 }}
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                                            />
                                        </svg>
                                    )}
                                </BookingServiceName>
                                <BookingMeta>
                                    {formatBookingDate(booking.date)} às {formatTime(booking.time)}
                                    {booking.worker && ` • ${booking.worker.name}`}
                                </BookingMeta>
                                {booking.confirmation?.confirmed && (
                                    <div style={{ marginTop: 6 }}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                fontSize: '0.75rem',
                                                backgroundColor: vars.successTint,
                                                color: vars.foregroundColor,
                                                border: `1px solid ${vars.dividerColor}`,
                                                borderRadius: 4,
                                            }}
                                        >
                                            Confirmado
                                        </span>
                                    </div>
                                )}
                                {booking.payment?.paid && (
                                    <div style={{ marginTop: 6 }}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                fontSize: '0.75rem',
                                                backgroundColor: '#E0D0A0',
                                                color: vars.foregroundColor,
                                                border: `1px solid ${vars.dividerColor}`,
                                                borderRadius: 4,
                                            }}
                                        >
                                            Pago
                                        </span>
                                    </div>
                                )}
                                {booking.isLinkedToSubscription && (
                                    <div style={{ marginTop: 6 }}>
                                        <span
                                            style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                fontSize: '0.75rem',
                                                backgroundColor: vars.backgroundColor,
                                                color: '#666',
                                                border: `1px solid ${vars.dividerColor}`,
                                                borderRadius: 4,
                                            }}
                                        >
                                            Assinatura
                                        </span>
                                    </div>
                                )}
                                {booking.notes && <BookingNotes>{booking.notes}</BookingNotes>}
                            </BookingInfo>
                            <BookingActions>
                                {booking.payment && !booking.payment.paid ? (
                                    <Button
                                        onClick={() => {
                                            gtagEvent('bookings_pay_click');
                                            AppRouting.navigate(`/booking/${booking.id}`);
                                        }}
                                    >
                                        Pagar
                                    </Button>
                                ) : booking.confirmation?.confirmed === false ? (
                                    <Button
                                        onClick={() => {
                                            gtagEvent('bookings_confirm_click');
                                            AppRouting.navigate(`/booking/${booking.id}`);
                                        }}
                                    >
                                        Confirmar
                                    </Button>
                                ) : booking.intakeStatus === 'pending' ? (
                                    <Button onClick={() => AppRouting.navigate(`/bookings/${booking.id}/intake`)}>
                                        {(booking.pendingIntakeCount ?? 0) > 1
                                            ? `Formulários (${booking.pendingIntakeCount})`
                                            : 'Formulário'}
                                    </Button>
                                ) : (
                                    <Button
                                        danger
                                        onClick={() => onCancelBooking(booking)}
                                        data-testid="cancel-booking-btn"
                                    >
                                        Cancelar
                                    </Button>
                                )}
                            </BookingActions>
                        </BookingCard>
                    ))
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: '#888' }} data-testid="empty-bookings">
                        Você não tem agendamentos.
                    </div>
                )}
            </div>
            <ButtonGroup style={{ marginTop: 24, justifyContent: 'center' }}>
                <Button primary onClick={onNewBooking} data-testid="new-booking-btn">
                    Novo Agendamento
                </Button>
            </ButtonGroup>
        </>
    );
}
